import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useOrganization, useOrganizations } from '../hooks/useOrganizations';
import type { Organization, OrganizationListItem, OrganizationRole } from '../types/organization';
import { ROLE_HIERARCHY } from '../types/organization';

interface OrganizationContextValue {
  currentOrg: Organization | null;
  currentOrgId: string | null;
  currentRole: OrganizationRole | null;
  organizations: OrganizationListItem[];
  isLoading: boolean;
  isOrgsLoading: boolean;
  error: Error | null;
  setCurrentOrgId: (orgId: string | null) => void;
  clearCurrentOrg: () => void;
  // Role-based permission helpers
  isOwner: boolean;
  isAdmin: boolean;
  canManageMembers: boolean;
  canUpdateOrg: boolean;
  canDeleteOrg: boolean;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

const CURRENT_ORG_KEY = 'authnz_current_org_id';

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(() => {
    return localStorage.getItem(CURRENT_ORG_KEY);
  });

  const { data: organizations, isLoading: isOrgsLoading } = useOrganizations();

  const { data: currentOrg, isLoading, error } = useOrganization(currentOrgId ?? undefined);

  // Persist org ID to localStorage
  const setCurrentOrgId = useCallback((orgId: string | null) => {
    setCurrentOrgIdState(orgId);
    if (orgId) {
      localStorage.setItem(CURRENT_ORG_KEY, orgId);
    } else {
      localStorage.removeItem(CURRENT_ORG_KEY);
    }
  }, []);

  const clearCurrentOrg = useCallback(() => {
    setCurrentOrgId(null);
  }, [setCurrentOrgId]);

  // Track org IDs that have errored so auto-default does not re-select them
  // (otherwise the error effect and auto-default effect can ping-pong infinitely).
  const [failedOrgIds, setFailedOrgIds] = useState<Set<string>>(new Set());

  // Clear org if user is no longer a member (404 response)
  useEffect(() => {
    if (error && currentOrgId) {
      const failed = currentOrgId;
      setFailedOrgIds(prev => (prev.has(failed) ? prev : new Set([...prev, failed])));
      clearCurrentOrg();
    }
  }, [error, currentOrgId, clearCurrentOrg]);

  // Auto-default to the first non-failed org when none is selected
  // (or the stored one is no longer accessible).
  useEffect(() => {
    if (isOrgsLoading || !organizations || organizations.length === 0) return;
    const hasMatch = currentOrgId && organizations.some(o => o.id === currentOrgId);
    if (!hasMatch) {
      const next = organizations.find(o => !failedOrgIds.has(o.id));
      if (next) setCurrentOrgId(next.id);
    }
  }, [organizations, isOrgsLoading, currentOrgId, setCurrentOrgId, failedOrgIds]);

  const currentRole = currentOrg?.role ?? null;

  // Permission helpers based on role hierarchy
  const roleAtLeast = (minRole: OrganizationRole): boolean => {
    if (!currentRole) return false;
    return ROLE_HIERARCHY[currentRole] >= ROLE_HIERARCHY[minRole];
  };

  const isOwner = currentRole === 'owner';
  const isAdmin = roleAtLeast('admin');
  const canManageMembers = roleAtLeast('admin');
  const canUpdateOrg = roleAtLeast('admin');
  const canDeleteOrg = isOwner;

  return (
    <OrganizationContext.Provider
      value={{
        currentOrg: currentOrg ?? null,
        currentOrgId,
        currentRole,
        organizations: organizations ?? [],
        isLoading,
        isOrgsLoading,
        error: error as Error | null,
        setCurrentOrgId,
        clearCurrentOrg,
        isOwner,
        isAdmin,
        canManageMembers,
        canUpdateOrg,
        canDeleteOrg,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useCurrentOrganization() {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useCurrentOrganization must be used within an OrganizationProvider');
  }
  return context;
}
